<template>
  <v-card class="orders-panel" elevation="0">
    <div class="orders-panel__heading">
      <div>
        <h2>Orders</h2>
        <p>Request stock, record what was sent, then confirm the studio count.</p>
      </div>
      <v-btn v-if="canEdit" color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">New Order</v-btn>
    </div>

    <div v-if="loading" class="orders-panel__loading"><v-progress-circular indeterminate color="primary"></v-progress-circular></div>
    <div v-else-if="!orders.length" class="orders-panel__empty">
      <v-icon size="34" color="primary">mdi-clipboard-list-outline</v-icon>
      <strong>No orders yet</strong>
      <p>Create an order when a studio needs more products.</p>
    </div>
    <div v-else class="orders-table-wrap">
      <table class="orders-table">
        <thead>
          <tr>
            <th>Order</th>
            <th>Status</th>
            <th>Created</th>
            <th class="number-cell">Items</th>
            <th class="number-cell">Requested</th>
            <th class="number-cell">Sent</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="order in orders" :key="order.id">
            <td><strong>#{{ order.id }}</strong></td>
            <td><v-chip :color="statusColor(order.status)" size="small" variant="tonal">{{ statusLabel(order.status) }}</v-chip></td>
            <td>{{ formatDate(order.created_at) }}</td>
            <td class="number-cell">{{ order.line_count }}</td>
            <td class="number-cell">{{ order.requested_units }}</td>
            <td class="number-cell">{{ order.sent_units }}</td>
            <td class="actions-cell"><v-btn size="small" variant="text" color="primary" @click="openOrder(order.id)">Open</v-btn></td>
          </tr>
        </tbody>
      </table>
    </div>

    <v-dialog v-model="createDialog" max-width="680">
      <v-card>
        <v-card-title class="dialog-title">New Inventory Order</v-card-title>
        <v-card-text>
          <div class="new-line-grid">
            <v-autocomplete
              v-model="newLine.itemId"
              :items="availableItems"
              item-title="name"
              item-value="id"
              label="Item"
              variant="outlined"
              hide-details
            ></v-autocomplete>
            <v-text-field
              v-model.number="newLine.requestedQuantity"
              label="Quantity"
              min="1"
              type="number"
              variant="outlined"
              hide-details
            ></v-text-field>
            <v-btn height="56" color="primary" variant="tonal" @click="addDraftLine">Add</v-btn>
          </div>

          <div v-if="draftLines.length" class="draft-lines">
            <div v-for="line in draftLines" :key="line.itemId" class="draft-line">
              <div><strong>{{ itemName(line.itemId) }}</strong><span>{{ line.requestedQuantity }} requested</span></div>
              <v-btn icon="mdi-close" size="small" variant="text" @click="removeDraftLine(line.itemId)"></v-btn>
            </div>
          </div>

          <v-textarea v-model="createNotes" class="mt-4" label="Order notes" rows="2" variant="outlined"></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="createDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="saving" @click="createOrder">Create Order</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="orderDialog" max-width="860" scrollable>
      <v-card v-if="selectedOrder">
        <v-card-title class="order-dialog-heading">
          <div>
            <span>Order #{{ selectedOrder.id }}</span>
            <v-chip :color="statusColor(selectedOrder.status)" size="small" variant="tonal">{{ statusLabel(selectedOrder.status) }}</v-chip>
          </div>
          <v-btn icon="mdi-close" variant="text" @click="orderDialog = false"></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-alert v-if="canEdit && isProcessingStage" class="mb-4" color="primary" variant="tonal">
            {{ showCosts
              ? 'Enter what can actually be sent. Costs and prices are entered once here; the studio will only confirm the received count.'
              : 'Enter what can actually be sent. The studio will only confirm the received count.' }}
          </v-alert>
          <v-alert v-else-if="canEdit && isReceivingStage" class="mb-4" color="info" variant="tonal">
            Count the products that physically arrived. Product details are already recorded.
          </v-alert>

          <div class="order-lines">
            <div v-for="line in selectedOrder.lines" :key="line.id" class="order-line-card">
              <div class="order-line-card__title">
                <div><strong>{{ line.item_name }}</strong><span>{{ line.category_name || 'Uncategorized' }}</span></div>
                <div class="requested-badge">Requested <strong>{{ line.requested_quantity }}</strong></div>
              </div>

              <div v-if="canEdit && isProcessingStage" :class="['processing-grid', { 'processing-grid--no-cost': !showCosts }]">
                <v-text-field v-model.number="line.sentQuantity" label="Quantity sent" min="0" type="number" variant="outlined"></v-text-field>
                <v-text-field v-if="showCosts" v-model.number="line.unitCost" label="Unit cost" min="0" prefix="$" type="number" variant="outlined"></v-text-field>
                <v-text-field v-model.number="line.sellingPrice" label="Selling price" min="0" prefix="$" type="number" variant="outlined"></v-text-field>
                <v-select
                  v-model="line.processStatus"
                  :items="fulfillmentOptions"
                  item-title="label"
                  item-value="value"
                  label="Status"
                  variant="outlined"
                ></v-select>
                <v-text-field
                  v-if="line.processStatus !== 'fulfilled'"
                  v-model="line.pendingReason"
                  class="processing-grid__reason"
                  label="Reason or note"
                  variant="outlined"
                ></v-text-field>
              </div>

              <div v-else-if="canEdit && isReceivingStage" class="receiving-grid">
                <div class="sent-summary"><span>Sent</span><strong>{{ line.sent_quantity }}</strong></div>
                <v-text-field v-model.number="line.receivedQuantity" label="Count received" min="0" type="number" variant="outlined"></v-text-field>
                <v-text-field v-model.number="line.damagedQuantity" label="Damaged quantity" min="0" type="number" variant="outlined"></v-text-field>
              </div>

              <div v-else :class="['completed-grid', { 'completed-grid--no-cost': !showCosts }]">
                <div><span>Sent</span><strong>{{ line.sent_quantity ?? 0 }}</strong></div>
                <div><span>Received</span><strong>{{ line.received_quantity ?? '—' }}</strong></div>
                <div v-if="showCosts"><span>Cost</span><strong>{{ formatCurrency(line.unit_cost) }}</strong></div>
                <div><span>Selling price</span><strong>{{ formatCurrency(line.selling_price) }}</strong></div>
                <v-chip :color="lineStatusColor(line.status)" size="small" variant="tonal">{{ statusLabel(line.status) }}</v-chip>
              </div>
            </div>
          </div>

          <v-textarea
            v-if="isProcessingStage"
            v-model="selectedOrder.notes"
            class="mt-4"
            label="Order notes"
            rows="2"
            variant="outlined"
          ></v-textarea>
        </v-card-text>
        <v-card-actions v-if="canEdit && (isProcessingStage || isReceivingStage)">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="orderDialog = false">Cancel</v-btn>
          <v-btn v-if="isProcessingStage" color="primary" :loading="saving" @click="processOrder">Save & Mark Sent</v-btn>
          <v-btn v-else color="primary" :loading="saving" @click="receiveOrder">Confirm Receipt</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="2800">{{ snackbar.message }}</v-snackbar>
  </v-card>
</template>

<script>
import secureAxios from '@/utils/secureAxios';

export default {
  name: 'InventoryOrders',
  props: {
    canEdit: { type: Boolean, default: true },
    items: { type: Array, default: () => [] },
    refreshKey: { type: Number, default: 0 },
    showCosts: { type: Boolean, default: true },
  },
  emits: ['changed'],
  data() {
    return {
      orders: [],
      loading: false,
      saving: false,
      createDialog: false,
      orderDialog: false,
      selectedOrder: null,
      draftLines: [],
      newLine: { itemId: null, requestedQuantity: 1 },
      createNotes: '',
      fulfillmentOptions: [
        { label: 'Fulfilled', value: 'fulfilled' },
        { label: 'Partial', value: 'partial' },
        { label: 'Unavailable', value: 'unavailable' },
        { label: 'Replacement suggested', value: 'replacement_suggested' },
        { label: 'Cancelled', value: 'cancelled' },
      ],
      snackbar: { show: false, message: '', color: 'primary' },
    };
  },
  computed: {
    availableItems() {
      const used = new Set(this.draftLines.map(line => Number(line.itemId)));
      return this.items.filter(item => !used.has(Number(item.id)));
    },
    isProcessingStage() {
      return ['pending', 'processing', 'partially_fulfilled'].includes(this.selectedOrder?.status);
    },
    isReceivingStage() {
      return ['sent', 'awaiting_receipt', 'partially_received'].includes(this.selectedOrder?.status);
    },
  },
  watch: {
    refreshKey() { this.loadOrders(); },
  },
  mounted() {
    this.loadOrders();
  },
  methods: {
    async loadOrders() {
      this.loading = true;
      try {
        const response = await secureAxios.get('/api/inventory/orders');
        this.orders = response.data || [];
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to load orders', 'error');
      } finally {
        this.loading = false;
      }
    },
    openCreateDialog() {
      this.draftLines = [];
      this.newLine = { itemId: null, requestedQuantity: 1 };
      this.createNotes = '';
      this.createDialog = true;
    },
    addDraftLine() {
      const itemId = Number(this.newLine.itemId);
      const quantity = Number(this.newLine.requestedQuantity);
      if (!itemId || !Number.isInteger(quantity) || quantity < 1) return this.notify('Choose an item and quantity', 'warning');
      this.draftLines.push({ itemId, requestedQuantity: quantity });
      this.newLine = { itemId: null, requestedQuantity: 1 };
    },
    removeDraftLine(itemId) {
      this.draftLines = this.draftLines.filter(line => line.itemId !== itemId);
    },
    itemName(itemId) {
      return this.items.find(item => Number(item.id) === Number(itemId))?.name || 'Item';
    },
    itemRecord(itemId) {
      return this.items.find(item => Number(item.id) === Number(itemId));
    },
    async createOrder() {
      if (!this.draftLines.length) return this.notify('Add at least one item', 'warning');
      this.saving = true;
      try {
        await secureAxios.post('/api/inventory/orders', { lines: this.draftLines, notes: this.createNotes });
        this.createDialog = false;
        this.notify('Order created', 'success');
        await this.loadOrders();
        this.$emit('changed');
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to create order', 'error');
      } finally {
        this.saving = false;
      }
    },
    async openOrder(id) {
      try {
        const response = await secureAxios.get(`/api/inventory/orders/${id}`);
        const order = response.data;
        order.lines = order.lines.map(line => {
          const item = this.itemRecord(line.item_id);
          const sent = line.sent_quantity === null ? Number(line.requested_quantity) : Number(line.sent_quantity);
          return {
            ...line,
            sentQuantity: sent,
            unitCost: line.unit_cost === null ? Number(item?.average_unit_cost || 0) : Number(line.unit_cost),
            sellingPrice: line.selling_price === null ? Number(item?.default_selling_price || 0) : Number(line.selling_price),
            processStatus: line.status === 'pending'
              ? (sent === Number(line.requested_quantity) ? 'fulfilled' : 'partial')
              : line.status,
            pendingReason: line.pending_reason || '',
            receivedQuantity: line.received_quantity === null ? Number(line.sent_quantity || 0) : Number(line.received_quantity),
            damagedQuantity: line.status === 'damaged' ? Number(line.received_quantity || 0) : 0,
          };
        });
        this.selectedOrder = order;
        this.orderDialog = true;
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to open order', 'error');
      }
    },
    async processOrder() {
      this.saving = true;
      try {
        await secureAxios.put(`/api/inventory/orders/${this.selectedOrder.id}/process`, {
          notes: this.selectedOrder.notes,
          lines: this.selectedOrder.lines.map(line => ({
            id: line.id,
            sentQuantity: line.sentQuantity,
            unitCost: line.unitCost,
            sellingPrice: line.sellingPrice,
            status: line.processStatus,
            pendingReason: line.pendingReason,
          })),
        });
        this.orderDialog = false;
        this.notify('Order is ready for studio receipt', 'success');
        await this.loadOrders();
        this.$emit('changed');
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to process order', 'error');
      } finally {
        this.saving = false;
      }
    },
    async receiveOrder() {
      this.saving = true;
      try {
        await secureAxios.post(`/api/inventory/orders/${this.selectedOrder.id}/receive`, {
          lines: this.selectedOrder.lines
            .filter(line => Number(line.sent_quantity || 0) > 0)
            .map(line => ({ id: line.id, receivedQuantity: line.receivedQuantity, damagedQuantity: line.damagedQuantity })),
        });
        this.orderDialog = false;
        this.notify('Receipt confirmed and stock updated', 'success');
        await this.loadOrders();
        this.$emit('changed');
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to receive order', 'error');
      } finally {
        this.saving = false;
      }
    },
    statusLabel(value) {
      return String(value || '').replaceAll('_', ' ').replace(/\b\w/g, letter => letter.toUpperCase());
    },
    statusColor(status) {
      if (status === 'received') return 'success';
      if (['sent', 'awaiting_receipt'].includes(status)) return 'info';
      if (['partially_fulfilled', 'partially_received'].includes(status)) return 'warning';
      if (status === 'cancelled') return 'grey';
      return 'primary';
    },
    lineStatusColor(status) {
      if (status === 'received') return 'success';
      if (['received_short', 'received_extra', 'damaged', 'partial'].includes(status)) return 'warning';
      if (['unavailable', 'cancelled'].includes(status)) return 'grey';
      return 'primary';
    },
    formatDate(value) {
      if (!value) return '—';
      return new Intl.DateTimeFormat('en-CA', { dateStyle: 'medium' }).format(new Date(value));
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '—';
      return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(value));
    },
    notify(message, color = 'primary') {
      this.snackbar = { show: true, message, color };
    },
  },
};
</script>

<style scoped>
.orders-panel { border: 1px solid #dfe7f0; border-radius: 10px; overflow: hidden; }
.orders-panel__heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 20px 22px; border-bottom: 1px solid #edf1f5; }
.orders-panel h2 { margin: 0; color: #173553; font-size: 17px; }
.orders-panel p { margin: 4px 0 0; color: #6b7f93; font-size: 13px; }
.orders-panel__loading, .orders-panel__empty { display: flex; min-height: 300px; flex-direction: column; align-items: center; justify-content: center; gap: 8px; text-align: center; }
.orders-table-wrap { overflow-x: auto; }
.orders-table { width: 100%; border-collapse: collapse; }
.orders-table th, .orders-table td { padding: 13px 16px; border-bottom: 1px solid #edf1f5; color: #40536a; font-size: 12px; text-align: left; white-space: nowrap; }
.orders-table th { background: #f8fafc; color: #6b7f93; font-size: 10px; letter-spacing: .06em; text-transform: uppercase; }
.number-cell { text-align: right !important; }
.actions-cell { text-align: right !important; }
.dialog-title { color: #0a2647; font-size: 18px; font-weight: 700; }
.new-line-grid { display: grid; grid-template-columns: minmax(0, 1fr) 130px 80px; gap: 10px; }
.draft-lines { margin-top: 16px; border: 1px solid #e4eaf0; border-radius: 8px; }
.draft-line { display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; border-bottom: 1px solid #edf1f5; }
.draft-line:last-child { border-bottom: 0; }
.draft-line > div { display: flex; flex-direction: column; }
.draft-line span, .order-line-card__title span { color: #7d8b99; font-size: 11px; }
.order-dialog-heading { display: flex; align-items: center; justify-content: space-between; }
.order-dialog-heading > div { display: flex; align-items: center; gap: 10px; color: #0a2647; font-size: 18px; font-weight: 700; }
.order-lines { display: flex; flex-direction: column; gap: 12px; }
.order-line-card { padding: 15px; border: 1px solid #dfe7f0; border-radius: 9px; }
.order-line-card__title { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
.order-line-card__title > div:first-child { display: flex; flex-direction: column; }
.requested-badge { color: #6b7f93; font-size: 11px; }
.requested-badge strong { margin-left: 4px; color: #173553; font-size: 15px; }
.processing-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
.processing-grid--no-cost { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.processing-grid__reason { grid-column: 1 / -1; }
.receiving-grid { display: grid; grid-template-columns: 120px minmax(180px, 1fr) minmax(180px, 1fr); align-items: center; gap: 14px; }
.sent-summary { display: flex; flex-direction: column; }
.sent-summary span, .completed-grid span { color: #7d8b99; font-size: 11px; }
.sent-summary strong { color: #173553; font-size: 22px; }
.completed-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)) auto; align-items: center; gap: 14px; }
.completed-grid--no-cost { grid-template-columns: repeat(3, minmax(0, 1fr)) auto; }
.completed-grid > div { display: flex; flex-direction: column; }
@media (max-width: 760px) {
  .orders-panel__heading { align-items: stretch; flex-direction: column; }
  .orders-panel__heading .v-btn { align-self: flex-start; }
  .new-line-grid { grid-template-columns: 1fr 100px; }
  .new-line-grid .v-btn { grid-column: 1 / -1; }
  .processing-grid { grid-template-columns: 1fr 1fr; }
  .receiving-grid, .completed-grid { grid-template-columns: 1fr 1fr; }
}
</style>
